export const POWERS = ['tokens', 'see_hidden', 'map', 'dice', 'notes'];
/** What each power is, as the DM's ticks name it. */
export const POWER_LABELS = {
    tokens: { label: 'Tokens', hint: 'Move, edit, hide and reveal, add and remove tokens; run the turn tracker.' },
    see_hidden: { label: 'See hidden', hint: 'See the board as you do: hidden tokens, the DM layer, the fog, walls and lights.' },
    map: { label: 'Map', hint: 'The map, the grid, scenes, walls and doors, zones, lights and the fog tools.' },
    dice: { label: 'Dice', hint: 'Private rolls and the fudge band, group roll requests, rulings and the table\'s rules.' },
    notes: { label: 'Notes', hint: 'Your DM screen and campaign notes, the NPC roster, unsent handouts and pins, the rollable tables, the card decks, the table\'s music and sound.' },
};
/** Whose hidden rolls a guest DM's handover gives or withholds: `withheld`, else the main DM. */
export const withheldOf = (g) => g.withheld ?? [g.of];
/**
 * What a guest DM always holds (issue 1160; Tom's A: the hidden monsters always go across): tokens, the DM's board,
 * the map and the dice. Never offered as a choice, so the main DM cannot withhold them.
 */
export const GUEST_POWERS = ['tokens', 'see_hidden', 'map', 'dice'];
/** The two choices from the wire, each a boolean, or null. */
export function parseGuestChoices(v) {
    const b = (v ?? {});
    if (typeof v !== 'object' || v === null || typeof b.notes !== 'boolean' || typeof b.hiddenRolls !== 'boolean')
        return null;
    return { notes: b.notes, hiddenRolls: b.hiddenRolls };
}
/** A guest DM's powers: the four always, and `notes` when the main DM hands the notes over. */
export function guestPowers(c) {
    return POWERS.filter((p) => GUEST_POWERS.includes(p) || (p === 'notes' && c.notes));
}
/**
 * Whether a throw is the main DM's hidden roll withheld from this viewer (issue 1160): the viewer is a guest DM handed
 * the session without hidden rolls, and the throw is private (or rolled for someone) and the main DM's — written,
 * rolled or rolled-for by them. With no throw named, a fudge (`roll.amend`) the main DM made. A guest's own throws,
 * and the players', are theirs to run the session with.
 */
export function withholdsThrow(viewer, actorId, roll) {
    const g = viewer.guest;
    if (!g || g.hiddenRolls)
        return false;
    const whose = withheldOf(g);
    const theirs = (id) => typeof id === 'string' && whose.includes(id);
    if (!roll)
        return theirs(actorId);
    if (!roll.private && !roll.by)
        return false;
    return theirs(actorId) || theirs(roll.actor.id) || theirs(roll.by?.id);
}
/** The delegation a membership carries onto a viewer: the powers and, for the guest DM, the handover. */
export function delegationOf(m) {
    return { ...(m.powers ? { powers: [...m.powers] } : {}), ...(m.guest ? { guest: { of: m.guest.of, hiddenRolls: m.guest.hiddenRolls, ...(m.guest.withheld ? { withheld: [...m.guest.withheld] } : {}) } } : {}) };
}
/** The powers named, in the list's order, each once; anything else dropped. Null when it is not a list. */
export function parsePowers(v) {
    if (!Array.isArray(v))
        return null;
    if (v.some((p) => typeof p !== 'string' || !POWERS.includes(p)))
        return null;
    return POWERS.filter((p) => v.includes(p));
}
/** Whether this person may do what `need` names: the DM always; a co-DM with the power; nobody else. */
export function holds(who, need) {
    if (who.role === 'dm')
        return true;
    return need !== 'dm' && who.role === 'codm' && !!who.powers?.includes(need);
}
/** Whether this viewer is shown the DM's board (hidden things, the DM's fog, walls, lights, zones, the whole order). */
export const seesAsDm = (who) => holds(who, 'see_hidden');
/**
 * Every DM-gated act by kind, each assigned to exactly one power (the wire's request kinds beside the log's event
 * kinds). An act whose kind is absent is anyone's at the table, under the usual rules. A kind listed here is decided
 * for a co-DM WITH the power as the DM's act, and for one without it as a player's (which owns nothing).
 */
export const ACT_POWER = {
    // tokens: the things on the board and the turn order
    'object.add': 'tokens', 'object.remove': 'tokens', 'object.restore': 'tokens', 'object.duplicate': 'tokens',
    'object.rename': 'tokens', 'object.reveal': 'tokens', 'object.hide': 'tokens', 'object.assign': 'tokens',
    'object.lock': 'tokens', 'object.resize': 'tokens', 'object.layer': 'tokens', 'object.pc': 'tokens',
    'object.nameplate': 'tokens', 'object.marker': 'tokens', 'object.disposition': 'tokens', 'object.facts': 'tokens',
    'object.infer': 'tokens', 'object.noaoo': 'tokens', 'object.noextra': 'tokens', 'object.wiki': 'tokens', 'object.move': 'tokens', 'objects.move': 'tokens',
    'object.values': 'tokens', 'object.share': 'tokens', 'object.tell': 'tokens', 'object.rotate': 'tokens', 'object.dead': 'tokens',
    'object.follow': 'tokens', 'object.light': 'tokens', 'object.vision': 'tokens', 'object.auras': 'tokens',
    'object.spells': 'tokens', 'object.loadouts': 'tokens',
    'placement.set': 'tokens',
    'encounter.add': 'tokens', 'encounter.link': 'tokens', 'encounter.set': 'tokens', 'encounter.effects': 'tokens',
    'encounter.move': 'tokens', 'encounter.remove': 'tokens', 'encounter.roll': 'tokens', 'encounter.start': 'tokens',
    'encounter.end': 'tokens', 'encounter.back': 'tokens', 'encounter.next': 'tokens', 'encounter.action': 'tokens',
    'encounter.priority': 'tokens', 'encounter.sort': 'tokens', 'encounter.pool': 'tokens', 'encounter.refresh': 'tokens',
    'attack.request': 'tokens', 'area.resolve': 'tokens', 'test.request': 'tokens',
    // NPC dialog (qa-runs#1167): a co-DM who may already change tokens says and deletes lines
    'dialog.say': 'tokens', 'dialog.delete': 'tokens',
    // the staged attack (issue 1527), made a roll by the server like an attack
    'attack.staged': 'tokens',
    // a spell cast (issue 1530), made a roll by the server like an attack
    'spell.cast': 'tokens',
    // conjuring a spirit and a token's astral state (issue 1531)
    'spirit.conjure': 'tokens', 'object.astral': 'tokens', 'object.jack': 'tokens',
    // a System Test, and the host a decker's token is in (issue 1532)
    'matrix.test': 'tokens', 'object.matrix': 'tokens',
    // bringing a vehicle out and a vehicle test (issue 1533)
    'vehicle.place': 'tokens', 'vehicle.test': 'tokens',
    // a spirit's power (issue 1172)
    'spirit.power': 'tokens',
    // vehicle combat (issue 1174): a hit on a vehicle, a ram, a crash, dump shock, and the chase
    'vehicle.hit': 'tokens', 'vehicle.ram': 'tokens', 'vehicle.crash': 'tokens', 'vehicle.dump': 'tokens', 'chase.set': 'tokens',
    // the DM's Damage control on a token's condition monitor (issue 1526), made an `object.damage` by the server
    'object.hurt': 'tokens',
    // map: the scene, what is drawn on it and what hides it
    'grid.set': 'map', 'scene.set': 'map',
    'fog.reveal': 'map', 'fog.cover': 'map', 'fog.revealAll': 'map', 'fog.coverAll': 'map', 'fog.set': 'map',
    'wall.add': 'map', 'wall.set': 'map', 'wall.remove': 'map', 'door.set': 'map',
    'light.add': 'map', 'light.set': 'map', 'light.remove': 'map', 'light.level': 'map', 'weather.set': 'map',
    'zone.paint': 'map', 'zone.erase': 'map', 'zone.set': 'map', 'zone.remove': 'map', 'vision.set': 'map',
    'focus.clear': 'map', 'focus.clearAll': 'map', 'area.clear': 'map', 'area.clearAll': 'map',
    'draw.add': 'map', 'draw.addMany': 'map', 'draw.erase': 'map', 'draw.eraseMine': 'map',
    // dice: the DM's private dice, the fudge, requests, rulings and the rules
    'roll.amend': 'dice', 'attack.ac': 'dice', 'attack.answer': 'dice', 'test.karma': 'dice', 'attack.apply': 'dice', 'save.bonus': 'dice', 'save.apply': 'dice',
    // a roll's total applied to tokens, and the DM's settling of it (qa-runs#406)
    'roll.apply': 'dice', 'roll.settle': 'dice',
    'roll.ask': 'dice', 'roll.ask.close': 'dice', 'log.clear': 'dice',
    'ruling.add': 'dice', 'ruling.keep': 'dice', 'ruling.remove': 'dice', 'session.end': 'dice', 'table.rules': 'dice',
    // notes: the DM's prep that reaches the table
    'handout.set': 'notes', 'handout.send': 'notes', 'handout.take': 'notes', 'handout.delete': 'notes',
    'pin.set': 'notes', 'pin.remove': 'notes', 'link.set': 'map', 'link.remove': 'map', 'table.roll': 'notes',
    // notes: card decks (issue 1177) — placing, shuffling and removing a deck, dealing to someone or to the table, and
    // discarding a card from the table. Without it a co-DM draws, plays, passes and reveals as a player does, from their own hand.
    'deck.add': 'notes', 'deck.shuffle': 'notes', 'deck.remove': 'notes', 'deck.draw': 'notes', 'card.discard': 'notes',
    // tokens: initiative by cards, an act on the turn order like the roll.
    'encounter.deal': 'tokens',
    // notes: the table's sound (issue 1161) — music, ambience and effects are the DM's prep played to the table
    'audio.play': 'notes', 'audio.next': 'notes', 'audio.stop': 'notes',
    // notes: the sound cues (issue 1162)
    'cue.set': 'notes', 'cue.remove': 'notes',
    // the DM's alone (issue 1169): a rewind, and Undo, which is one. It restores hidden things for everyone, it is chosen
    // from the scene's history (the history is `dm`'s), and it takes back what the co-DMs and the players did too.
    'scene.rewind': 'dm', 'scene.undo': 'dm',
};
/**
 * The requests the wire sends that are not themselves log events: the socket (or a route) turns each into one — a roll,
 * an attack, a template's saves, a roll on a table, an Undo (a rewind).
 */
export const REQUEST_KINDS = ['roll.request', 'attack.request', 'area.resolve', 'table.roll', 'scene.undo', 'test.request', 'object.hurt', 'attack.staged', 'spell.cast', 'spirit.conjure', 'matrix.test', 'vehicle.place', 'vehicle.test', 'spirit.power', 'blast.throw', 'rest.rolled', 'vehicle.hit', 'vehicle.ram', 'vehicle.crash', 'vehicle.dump'];
/**
 * The kinds decided the same for the DM, a co-DM and a player (by ownership, the turn, a hand of cards), so no power
 * makes them the DM's. A roll request is here: `powerOfAct` makes a private one `dice`'s. `roll` and `encounter.rolled`
 * are built by the server from a request (`roll.request`, `encounter.roll`), never sent as themselves.
 */
// A thrown blast, a rest that rolls, Delay and Act now (qa-runs#1171) are decided by ownership and the turn: the server's checks.
export const ANYONES_KINDS = ['roll.request', 'roll', 'encounter.rolled', 'area.set', 'focus.set', 'card.pass', 'card.play', 'card.reveal', 'blast.throw', 'rest.rolled', 'encounter.delay', 'encounter.act', 'encounter.ready', 'encounter.fire'];
/**
 * The power an act needs to be decided as the DM's, or null when it is anyone's. A roll is anyone's in public; private,
 * for a player, or in the fudge band it is `dice`'s.
 */
export function powerOfAct(body) {
    const b = (body ?? {});
    if (typeof b.kind !== 'string')
        return null;
    if (b.kind === 'roll.request')
        return b.private === true || (typeof b.forUserId === 'string') || (b.band !== undefined && b.band !== null) ? 'dice' : null;
    return ACT_POWER[b.kind] ?? null;
}
/**
 * The role an act is decided with: the DM's own; a co-DM's, the DM's when they hold the power it needs and a player's
 * otherwise (a player who owns nothing, so the act stands only where anyone's would). Anyone else's unchanged.
 */
export function actingRole(who, body) {
    if (who.role !== 'codm')
        return who.role;
    const need = powerOfAct(body);
    return need && holds(who, need) ? 'dm' : 'player';
}
/** The object ids an act names (the thing it changes), for the co-DM's "hidden means absent" check. */
export function actTargets(body) {
    const b = (body ?? {});
    if (b.kind === 'test.request')
        return typeof b.token === 'string' ? [b.token] : [];
    if (b.kind === 'roll.apply') {
        const t = body.targets;
        return Array.isArray(t) ? t.filter((x) => typeof x === 'string') : [];
    }
    if (b.kind === 'spell.cast')
        return [b.caster, b.target].filter((x) => typeof x === 'string');
    if (b.kind === 'spirit.conjure')
        return typeof b.conjurer === 'string' ? [b.conjurer] : [];
    if (typeof b.kind !== 'string' || !(b.kind.startsWith('object') || b.kind === 'attack.request' || b.kind === 'attack.staged'))
        return [];
    const out = [];
    if (typeof b.id === 'string')
        out.push(b.id);
    if (Array.isArray(b.ids))
        for (const x of b.ids)
            if (typeof x === 'string')
                out.push(x);
    if (Array.isArray(b.moves))
        for (const m of b.moves)
            if (m && typeof m.id === 'string')
                out.push(m.id);
    if (typeof b.attacker === 'string')
        out.push(b.attacker);
    if (typeof b.target === 'string')
        out.push(b.target);
    return out;
}
//# sourceMappingURL=codm.js.map